import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { TasksService } from 'app/modules/admin/apps/task-list/task-list.service';
import { environment } from 'environments/environment';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, combineLatestWith, map, timeout } from 'rxjs/operators';
import { UserService } from '../user/user.service';

interface ClassificationMenuItem {
    unitId: string;
    unitName: string;
    singleUnit: boolean;
    /** Janela de avaliação aberta agora. Fechada -> item aparece desabilitado. */
    isOpen?: boolean;
    /** Quando a janela ainda vai abrir sozinha (modo AUTO), a data de abertura. */
    opensAt?: string | null;
}

/** Selos do menu — as classes ficam no styles.scss (nav-badge-*). */
const BADGE_OPEN = 'nav-badge nav-badge-open';
const BADGE_CLOSED = 'nav-badge nav-badge-closed';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private taskListService: TasksService,
        private userService: UserService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        const tasks$ = this.taskListService.getTasks().pipe(
            timeout(8000),
            catchError(() => of(null))
        );

        // Menu dinâmico "Avaliar Iniciativas": o backend só retorna unidades em que
        // o usuário é do time de performance E a janela do cronograma está aberta.
        // Falha/lentidão não pode quebrar o menu -> catchError para [].
        const classification$ = this._httpClient
            .get<ClassificationMenuItem[]>(`${environment.url}classification/menu`)
            .pipe(timeout(8000), catchError(() => of([] as ClassificationMenuItem[])));

        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            combineLatestWith(tasks$, classification$),
            map(([navigation, tasks, classification]) => {
                // "Atividades" fica dentro do grupo "2. Acompanhe": busca recursiva.
                const tasksItem = this.findItem(navigation.default, 'tasks');
                if (tasksItem && tasks !== null && tasks.length) {
                    tasksItem.badge = {
                        title: tasks.length.toString(),
                        classes: 'px-2.5 bg-primary-400 text-black rounded-full'
                    };
                }

                // Primeiro monta o comitê, depois filtra: assim um grupo que ficou
                // vazio (sem permissão ou sem unidades) some junto com o cabeçalho.
                navigation.default = this.injectClassificationMenu(navigation.default, classification || []);
                const userPermissions = this.userService.getAllAccessGroups();
                navigation.default = this.filterNavigationByPermission(navigation.default, userPermissions);
                this._navigation.next(navigation);
                return navigation;
            })
        );
    }

    /**
     * Monta "Avaliar Iniciativas" dentro do grupo "3. Comitê".
     * Uma unidade -> item direto; várias -> item recolhível com uma linha por unidade.
     * Janela aberta: selo "Aberta". Fechada: item desabilitado com "Fechada" ou
     * "Abre dd/mm" — o usuário vê que a avaliação existe, só não está liberada.
     * Sem auth nos itens: o backend só devolve o que o usuário pode ver e a rota
     * tem GroupGuard. Os textos são traduzidos no LanguagesComponent (usa `meta`).
     */
    private injectClassificationMenu(items: any[], classification: ClassificationMenuItem[]): any[] {
        const group = this.findItem(items, 'flow.committee');
        if (!group) return items;
        if (!classification?.length) {
            group.children = [];
            return items;
        }

        // Abertas primeiro: é o que o usuário pode fazer agora.
        const units = [...classification].sort((a, b) =>
            Number(b.isOpen !== false) - Number(a.isOpen !== false) || a.unitName.localeCompare(b.unitName)
        );
        const toItem = (c: ClassificationMenuItem, title: string) => {
            const open = c.isOpen !== false; // backend antigo não manda isOpen: só listava abertas
            const opensAt = !open && c.opensAt ? new Date(c.opensAt) : null;
            const opensLabel = opensAt
                ? `${String(opensAt.getDate()).padStart(2, '0')}/${String(opensAt.getMonth() + 1).padStart(2, '0')}`
                : null;
            return {
                id: `apps.innovation.classification.${c.unitId}`,
                title,
                type: 'basic',
                icon: 'heroicons_outline:star',
                link: `/apps/innovation/classification/${c.unitId}`,
                disabled: !open,
                tooltip: open ? '' : opensLabel ? `A avaliação abre em ${opensLabel}` : 'A janela de avaliação está fechada',
                badge: {
                    title: open ? 'Aberta' : opensLabel ? `Abre ${opensLabel}` : 'Fechada',
                    classes: open ? BADGE_OPEN : BADGE_CLOSED,
                },
                meta: { classification: true, isOpen: open, opensLabel },
            };
        };

        const openCount = units.filter((c) => c.isOpen !== false).length;
        if (units.length === 1) {
            group.children = [{ ...toItem(units[0], 'Avaliar Iniciativas'), subtitle: units[0].unitName.trim() }];
        } else {
            group.children = [{
                id: 'apps.innovation.classification',
                title: 'Avaliar Iniciativas',
                subtitle: `${openCount} de ${units.length} unidades abertas`,
                type: 'collapsable',
                icon: 'heroicons_outline:star',
                meta: { classificationParent: true, openCount, total: units.length },
                children: units.map((c) => toItem(c, c.unitName.trim())),
            }];
        }
        return items;
    }

    /** Busca um item pelo id em qualquer nível do menu. */
    private findItem(items: any[], id: string): any | null {
        for (const item of items || []) {
            if (item.id === id) return item;
            const child = this.findItem(item.children, id);
            if (child) return child;
        }
        return null;
    }

    /**
     * Filter navigation items by permission
     */
    private filterNavigationByPermission(items: any[], permissions: string[]): any[] {
        const safePermissions = permissions || [];
        return items
            .filter(item => {
                if (!item.auth) return true;
                return item.auth.some(auth =>
                    safePermissions.some(p => p === auth || p.startsWith(auth + '-'))
                );
            })
            .map(item => ({
                ...item,
                children: item.children ? this.filterNavigationByPermission(item.children, safePermissions) : undefined
            }))
            // Grupo (ex.: "Administração") sem nenhum item visível não aparece como cabeçalho solto.
            .filter(item => item.type !== 'group' || item.children?.length > 0);
    }
}
